import {
  IngredientResult,
  SubProductIngredientResult,
  SubProductResult,
} from '@shared/api/types';

export type PdfIngredientRowKind =
  | 'ingredient'
  | 'processing'
  | 'packaging'
  | 'transport';

export interface PdfIngredientRow {
  id: string;
  kind: PdfIngredientRowKind;
  name: string;
  /** 0 for ingredients in the product's own recipe, +1 per sub-product level. */
  depth: number;
  /** Grams. Null for processing / packaging / transport rows. */
  weight: number | null;
  /** kgCO2e, already scaled to the quantity used in the top-level recipe. */
  emissions: number;
  emissionsCategory: string | null;
  carbonIntensity: number | null;
  /** True when this ingredient is a sub-product whose breakdown follows. */
  hasChildren: boolean;
}

type AnyIngredientRow = IngredientResult | SubProductIngredientResult;

function byEmissionsDesc(a: AnyIngredientRow, b: AnyIngredientRow) {
  return b.emissions - a.emissions;
}

function buildPptRows(
  subProduct: SubProductResult,
  depth: number,
  idPrefix: string
): PdfIngredientRow[] {
  const stages: Array<{
    kind: Exclude<PdfIngredientRowKind, 'ingredient'>;
    name: string;
    emissions: number;
  }> = [
    {
      kind: 'processing',
      name: 'Processing',
      emissions: subProduct.processingEmissions,
    },
    {
      kind: 'packaging',
      name: 'Packaging',
      emissions: subProduct.packagingEmissions,
    },
    {
      kind: 'transport',
      name: 'Transport',
      emissions: subProduct.transportEmissions,
    },
  ];

  return stages
    .filter((stage) => stage.emissions > 0)
    .map((stage) => ({
      id: `${idPrefix}/${stage.kind}`,
      kind: stage.kind,
      name: stage.name,
      depth,
      weight: null,
      emissions: stage.emissions,
      emissionsCategory: null,
      carbonIntensity: null,
      hasChildren: false,
    }));
}

function expandIngredients(
  ingredients: readonly AnyIngredientRow[],
  depth: number,
  idPrefix: string
): PdfIngredientRow[] {
  return [...ingredients].sort(byEmissionsDesc).flatMap((ingredient, index) => {
    const id = `${idPrefix}/${index}`;
    // The API has already scaled a sub-product's breakdown to the quantity
    // used in this recipe, from the stored assessment result.
    const subProduct = ingredient.subProductResult ?? null;

    const row: PdfIngredientRow = {
      id,
      kind: 'ingredient',
      name: ingredient.longDesc,
      depth,
      weight: ingredient.weight,
      emissions: ingredient.emissions,
      emissionsCategory: ingredient.emissionsCategory,
      carbonIntensity: ingredient.totalEmissionsPerKg,
      hasChildren: !!subProduct,
    };

    if (!subProduct) return [row];

    return [
      row,
      ...expandIngredients(subProduct.ingredients, depth + 1, id),
      ...buildPptRows(subProduct, depth + 1, id),
    ];
  });
}

/**
 * Flattens a product's recipe into table rows, ordered by emissions, with
 * sub-products expanded into indented child rows (their ingredients followed
 * by their processing, packaging and transport).
 */
export function buildPdfIngredientRows(
  ingredients: readonly IngredientResult[]
): PdfIngredientRow[] {
  return expandIngredients(ingredients, 0, 'root');
}
